import cn from 'classnames';
import Image from 'components/shared/image';
import SiteLink from 'components/shared/sitelink';
import HeartIcon from 'components/shared/heart-icon';
import Icon from 'components/shared/icon';
import UnitPrice from 'components/shared/unit-price/unitPrice';
import { useGlobalModules } from 'contexts';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useUI, useWishlist, useLocalWishlist, useLineItemActions } from 'hooks';
import { useRouter } from 'next/router';
import { formatPriceByStoreLocale } from 'lib/shopify';
import announce from 'lib/utils/announce';
import { useEffect, useRef, useState } from 'react';
import SubscriptionToggle from './subscriptionToggle';
import styles from './cartLineItem.module.scss';

const MAX_SELECT_QTY = 10;

const CartLineItem = ({ locale, lineItem, index, contentfulData, cart }) => {
  const { addToWishlist } = useWishlist();
  const { localWishlist } = useLocalWishlist();
  const globalModules = useGlobalModules();
  const router = useRouter();
  const { closeCartPopup, openConfirmWishlistPopup, displayCartPopup } = useUI();
  const [showOverlay, setShowOverlay] = useState(false);
  const cancelButtonRef = useRef(null);
  const removeButtonRef = useRef(null);

  const lineitemModules = globalModules?.translations?.lineItem || {};
  const { translations, colors } = globalModules || {};
  const slug = contentfulData?.slug;
  const title = contentfulData?.title || lineItem?.merchandise?.product?.title || '';

  useEffect(() => {
    if (showOverlay) cancelButtonRef.current?.focus();
  }, [showOverlay]);

  useEffect(() => {
    if (!displayCartPopup) setShowOverlay(false);
  }, [displayCartPopup]);

  const {
    handleRemoveProduct,
    handleUpdateQuantity,
    handleUpdateSellingPlan,
    getPrices,
    hasFreeItemTag,
    hasFreeItemCustomAttribute,
    lineItemIsFreeItem,
  } = useLineItemActions({ locale, lineItem, cart, contentfulData });

  const isGiftLine = Boolean(hasFreeItemTag || hasFreeItemCustomAttribute || lineItemIsFreeItem);

  const Prices = () => {
    const { finalPrice, discountedPrice } = getPrices();

    const getPriceText = price => {
      if (price === '0.00' && translations?.freeItem?.priceFreeText) {
        return (
          <span style={{ color: colors?.freeItem?.priceFreeTextColor || '' }}>
            {translations?.freeItem?.priceFreeText}
          </span>
        );
      }
      return formatPriceByStoreLocale({ locale, price });
    };

    return (
      <span className={styles.price}>
        <span className={cn({ [styles.lineThrough]: discountedPrice })}>
          {getPriceText(finalPrice)}
        </span>
        {discountedPrice && (
          <span className={styles.discount}>{getPriceText(discountedPrice)}</span>
        )}
        <UnitPrice
          unitPrice={{
            ...(lineItem.merchandise?.unitPrice || {}),
            ...(lineItem.merchandise?.unitPriceMeasurement || {}),
          }}
          price={lineItem.merchandise?.priceV2?.amount}
          className={styles.unitPrice}
          locale={locale}
          size={contentfulData?.size}
        />
      </span>
    );
  };

  const LineItemImage = () => {
    const img = (
      <div className={styles.itemImage}>
        <Image src={lineItem.merchandise?.image} layout="fill" />
      </div>
    );
    if (!slug || isGiftLine) return img;
    return (
      <SiteLink
        locale={locale}
        href={addLocaleToHref(`p/${slug}`, locale, router)}
        onClick={closeCartPopup}
        aria-label={title}
      >
        {img}
      </SiteLink>
    );
  };

  const handleSaveForLater = async event => {
    // React clears event.currentTarget after the handler returns. Read the dialog before the await.
    const dialog = event.currentTarget.closest('[role="dialog"]');
    const GIDs = [lineItem?.merchandise?.product?.id];
    await addToWishlist(GIDs);
    announce(`${title} ${translations?.accessibility?.savedForLater || 'saved for later'}`);
    openConfirmWishlistPopup(lineItem);
    dialog?.focus();
    handleRemoveProduct();
  };

  const showWishlistHeart =
    !isGiftLine && !localWishlist.includes(lineItem?.merchandise?.product?.id);

  return (
    <div className={cn(styles.lineItem, index !== 0 && styles.topBorder)}>
      <LineItemImage />
      <div className={styles.infoContainer}>
        <div className={styles.titleRow}>
          <div>
            <div className={cn(styles.title, { [styles.opacTitle]: showOverlay })}>{title}</div>
            <div className={styles.size}>
              {contentfulData?.size || lineItem.sellingPlanAllocation?.sellingPlan?.name || ''}
            </div>
          </div>
          {showWishlistHeart && (
            <button
              type="button"
              className={styles.heartBtn}
              onClick={handleSaveForLater}
              aria-label={`${lineitemModules?.saveForLaterText || 'Save for later'}: ${title}`}
            >
              <HeartIcon />
            </button>
          )}
        </div>
        <div className={styles.bottomRow}>
          <Prices />
          <div className={styles.controls}>
            {!lineItemIsFreeItem && (
              <button
                type="button"
                ref={removeButtonRef}
                className={styles.trashBtn}
                onClick={() => setShowOverlay(true)}
                aria-label={`${lineitemModules?.removeText || 'Remove'}: ${title}`}
              >
                <Icon type="trash" />
              </button>
            )}
            <select
              className={styles.qtySelect}
              value={lineItem.quantity}
              disabled={isGiftLine}
              aria-label={`${lineitemModules?.quantityLabel || 'Quantity'}: ${title}`}
              onChange={e => handleUpdateQuantity(Number(e.target.value))}
            >
              {Array.from(
                { length: Math.max(MAX_SELECT_QTY, lineItem.quantity) },
                (_, i) => i + 1
              ).map(q => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
          </div>
        </div>
        {lineItem?.discountAllocations?.[0]?.title ? (
          <div className={styles.attributes}>{lineItem.discountAllocations[0].title}</div>
        ) : null}
      </div>
      {!isGiftLine && (
        <SubscriptionToggle lineItem={lineItem} onUpdateSellingPlan={handleUpdateSellingPlan} />
      )}
      {showOverlay && (
        <div
          className={styles.confirmOverlay}
          role="alertdialog"
          aria-label={`${lineitemModules?.confirmRemove?.message || ''} ${title}`.trim()}
        >
          <span className={styles.overlayText}>{lineitemModules?.confirmRemove?.message}</span>
          <div className={styles.overlayButtons}>
            <button
              type="button"
              ref={cancelButtonRef}
              onClick={() => {
                setShowOverlay(false);
                removeButtonRef.current?.focus();
              }}
              className={styles.cancelButton}
            >
              {lineitemModules?.confirmRemove?.noButtonText}
            </button>
            <button
              type="button"
              onClick={event => {
                event.currentTarget.closest('[role="dialog"]')?.focus();
                handleRemoveProduct();
                announce(
                  `${title} ${translations?.accessibility?.removedFromCart || 'removed from cart'}`
                );
              }}
              className={styles.confirmButton}
            >
              {lineitemModules?.confirmRemove?.yesButtonText}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartLineItem;
